/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable } from '@angular/core';
import { forkJoin, Observable, Observer } from 'rxjs';

declare var document: any;

@Injectable()
export class ScriptLoaderService {
  static readonly SCRIPTS = {
    BRAINTREE: 'https://js.braintreegateway.com/web/3.38.0/js/client.min.js',
    BRAINTREE_HOSTED_FIELDS: 'https://js.braintreegateway.com/web/3.38.0/js/hosted-fields.min.js',
    BITMOVIN_ANALYTICS: 'https://cdn.bitmovin.com/analytics/web/1/bitmovinanalytics.min.js',
    BITMOVIN_CSS: 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css',
  };

  private scriptStatuses: any = {};

  constructor() {}

  /**
   * Load multiple scripts
   * @param scripts array of strings with script urls
   */
  loadMultipleScripts(scripts: string[]) {
    return forkJoin(scripts.map((script) => this.loadScript(script, false)));
  }

  // tslint:disable-next-line:cognitive-complexity
  loadScript(src: string, loadAsync = false) {
    return new Observable<any>((observer: Observer<any>) => {
      // emit if already loaded
      if (this.scriptStatuses[src] && this.scriptStatuses[src].loaded) {
        observer.next({ script: src, loaded: true, status: 'Already Loaded' });
        observer.complete();
      } else {
        // load script
        this.scriptStatuses[src] = {
          src,
          loaded: false,
        };

        if (src.endsWith('.js')) {
          this.loadJsScript(src, loadAsync, observer);
        }

        if (src.endsWith('.css')) {
          this.loadStylesheet(src, observer);
        }
      }
    });
  }

  private loadJsScript(src: string, loadAsync: boolean, observer: Observer<any>) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    if (loadAsync) {
      script.async = true;
    }

    script.src = this.scriptStatuses[src].src;
    if (script.readyState) {
      // IE
      script.onreadystatechange = () => {
        if (script.readyState === 'loaded' || script.readyState === 'complete') {
          script.onreadystatechange = null;
          this.scriptStatuses[src].loaded = true;
          observer.next({ script: src, loaded: true, status: 'Loaded' });
          observer.complete();
        }
      };
    } else {
      // Others
      script.onload = () => {
        this.scriptStatuses[src].loaded = true;
        observer.next({ script: src, loaded: true, status: 'Loaded' });
        observer.complete();
      };
    }
    script.onerror = (error: any) => observer.error({ script: name, loaded: false, status: 'Loaded' });
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  private loadStylesheet(src: string, observer: Observer<any>) {
    const script = document.createElement('link');
    script.rel = 'stylesheet';
    script.href = this.scriptStatuses[src].src;
    if (script.readyState) {
      // IE
      script.onreadystatechange = () => {
        if (script.readyState === 'loaded' || script.readyState === 'complete') {
          script.onreadystatechange = null;
          this.scriptStatuses[src].loaded = true;
          observer.next({ script: src, loaded: true, status: 'Loaded' });
          observer.complete();
        }
      };
    } else {
      // Others
      script.onload = () => {
        this.scriptStatuses[src].loaded = true;
        observer.next({ script: src, loaded: true, status: 'Loaded' });
        observer.complete();
      };
    }
    script.onerror = (error: any) => observer.error({ script: name, loaded: false, status: 'Loaded' });
    document.getElementsByTagName('head')[0].appendChild(script);
  }
}
